/**
 * Beat-Your-Past-Self callout — UI Wave A.
 * Shows on Mock Exam results screens + Dashboard. Pulls from
 * /api/student/past-self-comparison. Specific deltas are way more
 * motivating than absolute numbers.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

function deltaTone(delta) {
  if (delta == null) return "neutral";
  if (delta >= 5) return "great";
  if (delta > 0) return "up";
  if (delta === 0) return "flat";
  return "down";
}

const TONE_STYLE = {
  great:   { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-800", icon: TrendingUp,   prefix: "📈 Crushing it" },
  up:      { bg: "bg-blue-50 border-blue-200",       text: "text-blue-800",   icon: TrendingUp,   prefix: "📈 You're improving" },
  flat:    { bg: "bg-slate-50 border-slate-200",     text: "text-slate-700",  icon: Minus,         prefix: "Steady" },
  down:    { bg: "bg-amber-50 border-amber-200",     text: "text-amber-800",  icon: TrendingDown, prefix: "Recovery time" },
  neutral: { bg: "bg-slate-50 border-slate-200",     text: "text-slate-700",  icon: Minus,         prefix: "" },
};

export default function PastSelfDelta({ variant = "full" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/student/past-self-comparison")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;
  const mock = data.mock_exam;
  const review = data.smart_review;
  if (!mock && !review) return null;

  return (
    <Card className="p-4 border-slate-200 space-y-3" data-testid="past-self-delta">
      <h3 className="font-display font-bold text-sm flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-blue-700" />
        Beat your past self
      </h3>

      {mock && (
        <Row
          label="Last mock exam"
          latest={mock.latest_pct}
          previous={mock.previous_pct}
          delta={mock.delta_pct}
          testId="past-self-mock"
        />
      )}
      {review && (
        <Row
          label="This week's recall"
          latest={review.this_week_pct}
          previous={review.previous_week_pct}
          delta={review.delta_pct}
          testId="past-self-review"
        />
      )}
    </Card>
  );
}

function Row({ label, latest, previous, delta, testId }) {
  const tone = TONE_STYLE[deltaTone(delta)];
  const Icon = tone.icon;
  return (
    <div className={`p-3 rounded-lg border ${tone.bg}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12px] font-semibold text-slate-700">{label}</span>
        <span className="text-base font-extrabold tabular-nums">{latest != null ? `${latest}%` : "—"}</span>
      </div>
      {delta != null ? (
        <div className={`flex items-center gap-1.5 mt-1 text-[12px] font-semibold ${tone.text}`}>
          <Icon className="w-3 h-3" />
          {delta > 0 ? "+" : ""}{delta}% vs previous
          <span className="text-slate-500 font-normal">({previous}%)</span>
          {tone.prefix && <span className="ml-auto text-[11px]">{tone.prefix}</span>}
        </div>
      ) : (
        <p className="text-[11px] text-slate-500 mt-0.5">Take one more {label.toLowerCase()} to unlock the comparison.</p>
      )}
    </div>
  );
}
